import { create } from 'zustand';
import {
  CommunityPlanListItem,
  CommunityPlanDetail,
  DestinationIntel,
} from '../types';
import {
  getCommunityPlans,
  getPlanDetailBySlug,
  getTrending,
  getPerfectTiming,
  getFeatured,
  forkPlan,
  markHelpful,
  type PlansFilterParams,
} from '../services/plansApi';
import { ApiError } from '../services/apiBase';

const STALE_TIME = 5 * 60 * 1000; // 5 minutes

export type DetailStatus = 'idle' | 'loading' | 'found' | 'not_found' | 'error';

// Module-scope and monotonic: an id is never reused in a page's life, so
// A→B→A navigations and out-of-order completions are told apart by equality.
let detailRequestCounter = 0;
export function nextDetailRequestId(): number {
  detailRequestCounter += 1;
  return detailRequestCounter;
}

interface PlansState {
  // Discovery data
  communityPlans: CommunityPlanListItem[];
  featured: CommunityPlanDetail | null;
  trending: DestinationIntel[];
  perfectTiming: DestinationIntel[];

  // Detail
  activePlanDetail: CommunityPlanDetail | null;
  /**
   * The request the detail fields answer. A page mints an id per navigation
   * (`nextDetailRequestId`) and reads the detail fields only while this
   * equals its own id — so a plan retained from an earlier navigation, or a
   * late answer to a superseded one, is never shown as this navigation's.
   */
  detailRequestId: number;
  detailSlug: string | null;
  detailStatus: DetailStatus;

  // Filters
  filters: PlansFilterParams;

  // Loading / error
  isLoadingDiscovery: boolean;
  isLoadingPlans: boolean;
  isLoadingDetail: boolean;
  error: string | null;
  /** Per-section errors for independent fallback (G-16) */
  sectionErrors: {
    featured?: string;
    trending?: string;
    perfectTiming?: string;
  };

  // SWR timestamps
  discoveryFetchedAt: number | null;
  plansFetchedAt: number | null;

  // Actions
  fetchDiscoveryFeeds: () => Promise<void>;
  fetchCommunityPlans: (params?: PlansFilterParams) => Promise<void>;
  /**
   * Never rejects: resolves to the detail, or to null on any failure. A 404
   * settles as `not_found` (no published CommunityPlan holds the slug), any
   * other failure as `error`. Applies its result only while `requestId` is
   * still the store's `detailRequestId`.
   */
  fetchPlanBySlug: (slug: string, requestId: number) => Promise<CommunityPlanDetail | null>;
  setFilters: (filters: PlansFilterParams) => void;
  forkPlan: (id: number, startDate?: string) => Promise<unknown>;
  markHelpful: (id: number) => Promise<void>;

  // Utility
  clearError: () => void;
  invalidate: () => void;
}

export const usePlansStore = create<PlansState>((set, get) => ({
  communityPlans: [],
  featured: null,
  trending: [],
  perfectTiming: [],
  activePlanDetail: null,
  detailRequestId: 0,
  detailSlug: null,
  detailStatus: 'idle',
  filters: {},
  isLoadingDiscovery: false,
  isLoadingPlans: false,
  isLoadingDetail: false,
  error: null,
  sectionErrors: {},
  discoveryFetchedAt: null,
  plansFetchedAt: null,

  fetchDiscoveryFeeds: async () => {
    const { discoveryFetchedAt } = get();
    if (discoveryFetchedAt && Date.now() - discoveryFetchedAt < STALE_TIME) return;

    set({ isLoadingDiscovery: true, error: null, sectionErrors: {} });

    // Each section fetches independently — per-section error fallback (G-16)
    const [featuredRes, trendingRes, timingRes] = await Promise.allSettled([
      getFeatured(),
      getTrending(),
      getPerfectTiming(),
    ]);

    const sectionErrors: PlansState['sectionErrors'] = {};
    const featured = featuredRes.status === 'fulfilled' ? featuredRes.value : (() => {
      sectionErrors.featured = 'Failed to load featured';
      return get().featured; // keep cached
    })();
    const trending = trendingRes.status === 'fulfilled' ? trendingRes.value : (() => {
      sectionErrors.trending = 'Failed to load trending';
      return get().trending; // show cached
    })();
    const perfectTiming = timingRes.status === 'fulfilled' ? timingRes.value : (() => {
      sectionErrors.perfectTiming = 'Failed to load timing';
      return get().perfectTiming;
    })();

    set({
      featured,
      trending,
      perfectTiming,
      isLoadingDiscovery: false,
      discoveryFetchedAt: Date.now(),
      sectionErrors,
    });
  },

  fetchCommunityPlans: async (params) => {
    set({ isLoadingPlans: true, error: null });
    try {
      const filters = params || get().filters;
      const data = await getCommunityPlans(filters);
      set({
        communityPlans: data,
        isLoadingPlans: false,
        plansFetchedAt: Date.now(),
        filters,
      });
    } catch (err) {
      set({
        isLoadingPlans: false,
        error: err instanceof Error ? err.message : 'Failed to load plans',
      });
    }
  },

  fetchPlanBySlug: async (slug, requestId) => {
    set({
      detailRequestId: requestId,
      detailSlug: slug,
      detailStatus: 'loading',
      activePlanDetail: null,
      isLoadingDetail: true,
      error: null,
    });
    try {
      const detail = await getPlanDetailBySlug(slug);
      if (get().detailRequestId === requestId) {
        set({ activePlanDetail: detail, detailStatus: 'found', isLoadingDetail: false });
      }
      return detail;
    } catch (err) {
      if (get().detailRequestId === requestId) {
        const notFound = err instanceof ApiError && err.status === 404;
        set({
          detailStatus: notFound ? 'not_found' : 'error',
          isLoadingDetail: false,
          error: err instanceof Error ? err.message : 'Plan not found',
        });
      }
      return null;
    }
  },

  setFilters: (filters) => {
    set({ filters });
    get().fetchCommunityPlans(filters);
  },

  forkPlan: async (id, startDate?) => {
    try {
      const result = await forkPlan(id, startDate);
      // Refresh community plans to reflect updated used_count
      get().fetchCommunityPlans();
      return result;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to save plan' });
      throw err;
    }
  },

  markHelpful: async (id) => {
    try {
      await markHelpful(id);
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to mark helpful' });
    }
  },

  clearError: () => set({ error: null }),
  invalidate: () => set({ discoveryFetchedAt: null, plansFetchedAt: null }),
}));

// Selector hooks
export const useCommunityPlans = () => usePlansStore((s) => s.communityPlans);
export const useFeaturedPlan = () => usePlansStore((s) => s.featured);
export const useTrending = () => usePlansStore((s) => s.trending);
export const usePerfectTiming = () => usePlansStore((s) => s.perfectTiming);
export const useActivePlanDetail = () => usePlansStore((s) => s.activePlanDetail);

export function isPlansStale(): boolean {
  const { discoveryFetchedAt } = usePlansStore.getState();
  if (!discoveryFetchedAt) return true;
  return Date.now() - discoveryFetchedAt > STALE_TIME;
}
